import { Injectable, inject } from '@angular/core';
import { CalAngularService, ConfigService } from '@cvx/cal-angular';
import { firstValueFrom } from 'rxjs';

/**
 * Authorization service that validates whether the signed-in user belongs to
 * any of the allowed Azure AD security groups.
 *
 * Group membership resolution and token acquisition are fully delegated to CAL
 * (`isInGroup`). Allowed group object IDs are read at runtime from the CAL
 * config file (`allowedSecurityGroups`), so they can be parameterized per
 * environment without rebuilding the app.
 */
@Injectable({
  providedIn: 'root'
})
export class SecurityGroupService {
  private readonly calService = inject(CalAngularService);
  private readonly configService = inject(ConfigService);

  private cachedMembership: boolean | null = null;

  async isUserInAllowedGroup(): Promise<boolean> {
    if (this.cachedMembership !== null) {
      return this.cachedMembership;
    }

    let allowedGroupIds: string[];
    let retryOnError: boolean;
    try {
      allowedGroupIds = this.getAllowedGroupIds();
      retryOnError = this.configService.getSettings('retrySecurityGroupCheckOnError') === true;
    } catch (error) {
      console.error('Error reading security group configuration:', error);
      this.cachedMembership = false;
      return false;
    }

    if (!allowedGroupIds.length) {
      console.warn('No allowed security groups configured (allowedSecurityGroups).');
      this.cachedMembership = false;
      return false;
    }

    const maxAttempts = retryOnError ? 2 : 1;
    for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex += 1) {
      try {
        const isMember = await firstValueFrom(this.calService.isInGroup(allowedGroupIds));
        this.cachedMembership = isMember;
        return isMember;
      } catch (error) {
        const isInteractionInProgress = error instanceof Error
          && error.message.toLowerCase().includes('interaction_in_progress');

        if (attemptIndex + 1 < maxAttempts && !isInteractionInProgress) {
          console.warn('Security group membership check failed; retrying once:', error);
          continue;
        }

        console.error('Error checking security group membership:', error);
        if (!retryOnError) {
          this.cachedMembership = false;
        }
        return false;
      }
    }

    return false;
  }

  private getAllowedGroupIds(): string[] {
    const configured = this.configService.getSettings('allowedSecurityGroups');
    if (!Array.isArray(configured)) {
      return [];
    }
    return configured
      .map((id: unknown) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id: string) => id.length > 0);
  }

  clearCache(): void {
    this.cachedMembership = null;
  }
}
